import { DEFAULT_TIMEZONE, HOME_COUNTRY } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Field, Icon, Input, RadioGroup, Select, Switch, toast } from "@sagot/ui";
import { Controller, FormProvider, useForm, useFormContext } from "react-hook-form";
import { FormAlert } from "../../../auth/components/form-alert";
import { useErrorMessage } from "../../../lib/api-errors";
import { bookingsHref, integrationsHref } from "../../../lib/booking-links";
import { TimezoneOptions } from "../../../lib/region-pickers";
import { zodResolver } from "../../../lib/zod-resolver";
import { usePermissions } from "../../../session/permissions";
import { useWorkspaceContext, useWorkspaceDetails } from "../../../session/workspace-context";
import { calendarStateOf, useCalendarIntegration } from "../../workspace/settings/use-google-calendar";
import type { Action } from "../shared/types";
import { useAvailabilitySchema } from "./availability-form";
import { LIMITS, availabilityFromAction, bookingModeOf, toAvailabilityConfig, type AvailabilityMode, type AvailabilityValues } from "./availability-model";
import { BlockedDatesEditor } from "./blocked-dates-editor";
import { BookingModeBanner } from "./booking-mode-banner";
import { HOURS_SECTION_ID, HoursEditor } from "./hours-editor";
import { useActionMutations } from "./use-actions";

const P = "build.actions.availability";
type NumberField = "slot_minutes" | "buffer_minutes" | "min_notice_hours" | "horizon_days";
const TIMING_FIELDS: readonly { name: NumberField; label: string; hint: string; bounds: { min: number; max: number } }[] = [
  { name: "slot_minutes", label: "slotMinutes", hint: "slotMinutesHint", bounds: LIMITS.slotMinutes },
  { name: "buffer_minutes", label: "bufferMinutes", hint: "bufferMinutesHint", bounds: LIMITS.bufferMinutes },
  { name: "min_notice_hours", label: "minNoticeHours", hint: "minNoticeHoursHint", bounds: LIMITS.minNoticeHours },
  { name: "horizon_days", label: "horizonDays", hint: "horizonDaysHint", bounds: LIMITS.horizonDays },
];

function ModeField({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation();
  const { control } = useFormContext<AvailabilityValues>();
  return (
    <div>
      <p id="availability-mode-label" className="label">{t(`${P}.mode.label`)}</p>
      <Controller
        control={control}
        name="mode"
        render={({ field }) => (
          <RadioGroup<AvailabilityMode>
            aria-labelledby="availability-mode-label"
            disabled={disabled}
            value={field.value}
            onValueChange={field.onChange}
            options={[
              { value: "auto", label: t(`${P}.mode.auto`), description: t(`${P}.mode.autoHint`) },
              { value: "request", label: t(`${P}.mode.request`), description: t(`${P}.mode.requestHint`) },
            ]}
          />
        )}
      />
    </div>
  );
}

function ZoneField({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation();
  const { control, formState } = useFormContext<AvailabilityValues>();
  const workspace = useWorkspaceDetails().data;
  const zone = workspace?.timezone ?? DEFAULT_TIMEZONE;
  return (
    <Field label={t(`${P}.timezone.label`)} hint={t(`${P}.timezone.hint`)} error={formState.errors.timezone?.message}>
      <Controller
        control={control}
        name="timezone"
        render={({ field }) => (
          <Select {...field} disabled={disabled}>
            <option value="">{t(`${P}.timezone.workspace`, { zone })}</option>
            <TimezoneOptions country={workspace?.country ?? HOME_COUNTRY} current={field.value || zone} format={(tz) => t(`settings.org.timezones.${tz.replace("/", "_")}`, { defaultValue: tz })} />
          </Select>
        )}
      />
    </Field>
  );
}

function TimingFields({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation();
  const { register, formState } = useFormContext<AvailabilityValues>();
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {TIMING_FIELDS.map(({ name, label, hint, bounds }) => (
        <Field key={name} label={t(`${P}.timing.${label}`)} hint={t(`${P}.timing.${hint}`)} error={formState.errors[name]?.message}>
          <Input type="number" inputMode="numeric" step={1} min={bounds.min} max={bounds.max} disabled={disabled} {...register(name, { valueAsNumber: true })} />
        </Field>
      ))}
    </div>
  );
}

interface AvailabilityEditorProps {
  agentId: string;
  /** The saved booking action; its `availability` seeds the form. */
  action: Action;
  canManage: boolean;
  onSaved: (action: Action) => void;
}

/** Availability section of a booking action: mode, hours, slot rules, closures, location, Meet. */
export function AvailabilityEditor({ agentId, action, canManage, onSaved }: AvailabilityEditorProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const { summary } = useWorkspaceContext();
  const permissions = usePermissions();
  const calendar = calendarStateOf(useCalendarIntegration(permissions.can("settings.view")).data);
  const { update } = useActionMutations(agentId);
  const form = useForm<AvailabilityValues>({ resolver: zodResolver(useAvailabilitySchema()), defaultValues: availabilityFromAction(action) });
  const disabled = !canManage;
  const mode = bookingModeOf(action) ?? "unconfigured";
  const submit = form.handleSubmit((values) => {
    update.mutate(
      { id: action.id, body: { availability: toAvailabilityConfig(values) } },
      {
        onSuccess: (saved) => {
          form.reset(availabilityFromAction(saved));
          toast.success(t(`${P}.saved`));
          onSaved(saved);
        },
      },
    );
  });
  const focusHours = () => {
    const section = document.getElementById(HOURS_SECTION_ID);
    section?.focus();
  };
  return (
    <section aria-labelledby="availability-title" className="space-y-5 rounded-lg border border-line p-4" data-testid="availability-editor">
      <div>
        <h3 id="availability-title" className="flex items-center gap-2 text-base font-bold">
          <Icon name="calendar" className="size-4 text-primary-ink" />
          {t(`${P}.title`)}
        </h3>
        <p className="mt-0.5 text-xs text-fg-subtle">{t(`${P}.subtitle`)}</p>
      </div>
      <BookingModeBanner
        mode={mode}
        calendarName={calendar.calendarName}
        calendarConnected={calendar.connected}
        bookingsHref={bookingsHref(summary.slug)}
        integrationsHref={integrationsHref(summary.slug)}
        onSetHours={focusHours}
      />
      <FormProvider {...form}>
        <form id="availability-form" noValidate className="space-y-6" onSubmit={(event) => void submit(event)}>
          {update.isError ? <FormAlert title={message(update.error)} /> : null}
          {disabled ? <p className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted">{t(`${P}.readOnly`)}</p> : null}
          <ModeField disabled={disabled} />
          <ZoneField disabled={disabled} />
          <HoursEditor disabled={disabled} />
          <TimingFields disabled={disabled} />
          <BlockedDatesEditor disabled={disabled} />
          <Field label={t(`${P}.location.label`)} hint={t(`${P}.location.hint`)} error={form.formState.errors.location?.message}>
            <Input maxLength={LIMITS.location} placeholder={t(`${P}.location.placeholder`)} disabled={disabled} {...form.register("location")} />
          </Field>
          {calendar.connected ? (
            <div className="flex items-start justify-between gap-4 rounded-lg border border-line p-4">
              <div>
                <label htmlFor="availability-meet" className="text-sm font-semibold">{t(`${P}.meet.label`)}</label>
                <p id="availability-meet-hint" className="mt-0.5 text-xs text-fg-subtle">{t(`${P}.meet.hint`)}</p>
              </div>
              <Controller control={form.control} name="create_meet" render={({ field }) => (
                <Switch id="availability-meet" aria-describedby="availability-meet-hint" checked={field.value} onCheckedChange={field.onChange} disabled={disabled} className="mt-0.5" />
              )} />
            </div>
          ) : null}
          {canManage ? (
            <div className="flex justify-end">
              <Button type="submit" loading={update.isPending}>
                <Icon name="check" />
                {t(`${P}.save`)}
              </Button>
            </div>
          ) : null}
        </form>
      </FormProvider>
    </section>
  );
}
